import { apiRequest } from './http';
import type { CreateRequestResponse } from './types';

// 단가 통화. KRW·JPY는 BI 반영 시 이벤트 발생일 환율로 USD 환산, USD는 그대로.
export type AfPriceCurrency = 'KRW' | 'JPY' | 'USD';
export type AfBillingBasis = 'event' | 'install';
export const AF_PRICE_CURRENCIES: readonly AfPriceCurrency[] = ['KRW', 'JPY', 'USD'];

export interface AfPriceRow {
  price_id: string;
  series_id: string;
  media_source: string;
  project_id: string | null;
  app_id: string;
  os: 'aos' | 'ios';
  country: string | null;
  campaign_name: string | null;
  event_name: string;
  billing_basis?: AfBillingBasis;
  unit_price: string;
  currency: AfPriceCurrency;
  vat_included: boolean;
  start_date: string;
  end_date: string;
  changed_at: string | null;
  changed_by_email: string | null;
  closed_at: string | null;
  closed_by_email: string | null;
  change_reason: string | null;
  // false 면 삭제(off) 행. 목록 API 는 off 행을 빼고 주지만 옛 응답엔 없을 수 있다.
  is_active?: boolean;
}

export interface AfPriceChangeInput {
  media_source: string;
  project_id: string;
  os: 'aos' | 'ios';
  country: string | null;
  campaign_name: string | null;
  event_name: string;
  billing_basis?: AfBillingBasis;
  unit_price: string;
  currency: AfPriceCurrency;
  vat_included: boolean;
  start_date: string;
  end_date: string | null;
  change_reason: string | null;
  // 삭제는 현재 값을 그대로 두고 이 값만 false 로 보내는 update 다.
  is_active?: boolean;
}

export interface AfPriceExpectedState {
  series_id: string;
  media_source: string;
  app_id: string;
  os: 'aos' | 'ios';
  country: string | null;
  campaign_name: string | null;
  event_name: string;
  billing_basis?: AfBillingBasis;
  unit_price: string;
  currency: AfPriceCurrency;
  vat_included: boolean;
  start_date: string;
  end_date: string;
  changed_at: string | null;
  is_active?: boolean;
}

export type AfPriceBatchOperation =
  | {
      type: 'insert';
      op_id: string;
      change: AfPriceChangeInput;
    }
  | {
      type: 'update';
      op_id: string;
      price_id: string;
      expected: AfPriceExpectedState;
      change: AfPriceChangeInput;
    };

export interface AfPriceBatchConflict {
  op_id: string;
  code: string;
  message: string;
  conflicting_op_ids: string[];
  conflicting_price_ids: string[];
}

export interface AfPriceBatchPlanItem {
  op_id: string;
  type: 'insert' | 'update';
  row: AfPriceRow;
  operations: AfPriceOperation[];
}

export interface AfPriceBatchResponse {
  items: AfPriceBatchPlanItem[];
  conflicts: AfPriceBatchConflict[];
  applied: boolean;
}

export interface AfPriceCostPreviewSeriesDaily {
  date: string;
  event_count: number;
  current_cost: string;
  new_cost: string;
  // 그날 단가 1단위의 USD 가치(부가세 제외). USD 단가는 "1", 환율 결측이면 null.
  current_fx_to_usd: string | null;
  new_fx_to_usd: string | null;
}

export interface AfPriceCostPreviewSeries {
  op_id: string;
  series_id: string;
  event_count: number;
  current_unit_price: string;
  new_unit_price: string;
  current_currency: AfPriceCurrency;
  new_currency: AfPriceCurrency;
  current_vat_included: boolean;
  new_vat_included: boolean;
  current_cost: string;
  new_cost: string;
  delta_cost: string;
  daily: AfPriceCostPreviewSeriesDaily[];
}

export interface AfPriceCostPreviewTotals {
  event_count: number;
  current_cost: string;
  new_cost: string;
  delta_cost: string;
  // 환율을 못 찾아 비용이 0으로 계산된 (시리즈, 날짜) 수.
  fx_missing_days: number;
}

export interface AfPriceCostPreviewDaily {
  date: string;
  current_cost: string;
  new_cost: string;
}

export interface AfPriceCostPreviewResponse {
  start_date: string;
  end_date: string;
  series: AfPriceCostPreviewSeries[];
  totals: AfPriceCostPreviewTotals;
  daily: AfPriceCostPreviewDaily[];
}

export interface AfPriceEventOption {
  campaign_name: string | null;
  event_name: string;
}

export interface AfPriceEventLookupInput {
  media_source: string;
  project_id: string;
  os: 'aos' | 'ios';
  country: string | null;
  campaign_name: string | null;
  start_date: string;
  billing_basis?: AfBillingBasis;
  end_date: string;
}

export type AfPriceOperation =
  | {
      type: 'close';
      price_id: string;
      expected_end_date: string;
      new_end_date: string;
    }
  | { type: 'insert'; row: AfPriceRow };

export interface AfPriceListInput {
  media_source?: string;
  app_id?: string;
  os?: 'aos' | 'ios';
  period_start?: string;
  period_end?: string;
}

export function fetchAfPrices(
  input: AfPriceListInput,
  signal?: AbortSignal,
): Promise<{ prices: AfPriceRow[] }> {
  return apiRequest<{ prices: AfPriceRow[] }>('/prices', {
    query: { include_closed: true, ...input, limit: 500 },
    signal,
  });
}

export function fetchAfPriceMediaSources(
  signal?: AbortSignal,
): Promise<{ media_sources: string[] }> {
  return apiRequest<{ media_sources: string[] }>('/prices/media-sources', { signal });
}

export function fetchAfPriceCountries(signal?: AbortSignal): Promise<{ countries: string[] }> {
  return apiRequest<{ countries: string[] }>('/prices/countries', { signal });
}

export function fetchAfPriceEventOptions(
  input: AfPriceEventLookupInput,
): Promise<{ options: AfPriceEventOption[] }> {
  return apiRequest<{ options: AfPriceEventOption[] }>('/prices/event-options', {
    query: { ...input },
  });
}

export function previewAfPriceBatch(
  operations: AfPriceBatchOperation[],
): Promise<AfPriceBatchResponse> {
  return apiRequest<AfPriceBatchResponse>('/prices/batch/preview', {
    method: 'POST',
    body: { operations },
  });
}

export function previewAfPriceBatchCost(
  operations: AfPriceBatchOperation[],
  signal?: AbortSignal,
): Promise<AfPriceCostPreviewResponse> {
  return apiRequest<AfPriceCostPreviewResponse>('/prices/batch/cost-preview', {
    method: 'POST',
    body: { operations },
    signal,
  });
}

export function createAfPriceApplyRequest(
  operations: AfPriceBatchOperation[],
): Promise<CreateRequestResponse> {
  return apiRequest<CreateRequestResponse>('/prices/batch', {
    method: 'POST',
    body: { operations },
  });
}

export type AfPriceLogAction = 'insert' | 'update' | 'close' | 'delete';
export type AfPriceLogRequestStatus = 'pending' | 'approved' | 'rejected';
export type AfPriceLogApplyStatus = 'pending' | 'done' | 'failed' | 'skipped';

export interface AfPriceLogValues {
  unit_price: string;
  currency: AfPriceCurrency;
  vat_included: boolean;
  start_date: string;
  end_date: string | null;
  billing_basis: AfBillingBasis;
  event_name: string;
  is_active: boolean;
}

export interface AfPriceLogEntry {
  request_id: number;
  request_status: AfPriceLogRequestStatus;
  apply_status: AfPriceLogApplyStatus | null;
  apply_error: string | null;
  requested_at: string;
  requester_email: string | null;
  decided_at: string | null;
  decided_by: string | null;
  op_id: string;
  project_id: string;
  action: AfPriceLogAction;
  media_source: string;
  os: 'aos' | 'ios';
  country: string | null;
  campaign_name: string | null;
  event_name: string;
  billing_basis: AfBillingBasis;
  before: AfPriceLogValues | null;
  after: AfPriceLogValues;
  change_reason: string | null;
}

export interface AfPriceLogListResponse {
  entries: AfPriceLogEntry[];
}

/** 단가 반영 요청을 작업 단위로 펼친 로그. projectId 가 null 이면 전체 프로젝트. 최신순. */
export function fetchPriceLogs(
  projectId: string | null,
  limit = 500,
  signal?: AbortSignal,
): Promise<AfPriceLogListResponse> {
  return apiRequest<AfPriceLogListResponse>('/prices/logs', {
    query: projectId ? { project_id: projectId, limit } : { limit },
    signal,
  });
}
